/**
 * Dexie Adapters Registration for Web
 *
 * Este archivo crea y registra los adapters Dexie en el DatabaseFactory.
 * Todos los repositorios ahora usan la arquitectura unificada.
 *
 * USO:
 * import { registerDexieAdapters } from './services/storage/adapters/dexie';
 * registerDexieAdapters();
 */
import { DatabaseFactory, DatabaseAdapters, Platform } from '../../DatabaseFactory';
import { DexieDatabase, getDexieDatabase } from './DexieDatabase';
import { DexieAdapter } from './DexieAdapter';
import { DexieSyncAdapter } from './DexieSyncAdapter';

// Repositorios unificados (arquitectura nueva)
import {
  ProfileRepository,
  GastoRepository,
  IngresoRepository,
  UbicacionRepository,
  UnidadRepository,
  EmpresaRepository,
  NotaRepository,
  EventoRepository,
  RecordatorioRepository,
  LluviaRepository,
  SigsaDeclaracionRepository,
  SyncAccionesRepository,
  ElementoMapaRepository,
  PuntoControlRepository,
  PuntoControlEventoRepository,
  SiembraLoteRepository,
  NotaAnclaRepository,
  PersonaCampoRepository,
  ApodoRepository,
  CategoriaCacheRepository,
  StockRepository,
  StockHistoricoRepository,
  AnimalHistoricoRepository,
  OperacionCompraVentaRepository,
  AnimalRepository,
  ConceptoRepository,
  CategoriaRepository
} from '../../repositories';

// Instancia de la base de datos
let _db: DexieDatabase | null = null;
let _dexieAdapter: DexieAdapter | null = null;
let _isRegistered: boolean = false;

/**
 * Obtiene la instancia de DexieDatabase (singleton)
 */
function getDb(): DexieDatabase {
  if (!_db) {
    _db = getDexieDatabase();
  }
  return _db;
}

/**
 * Registra los adapters Dexie en el DatabaseFactory
 *
 * Esto permite que el factory use Dexie/IndexedDB en web.
 * Debe llamarse una sola vez al inicio de la app.
 */
export function registerDexieAdapters(): void {
  if (_isRegistered) {
    console.log('[Web] Dexie adapters already registered');
    return;
  }

  const db = getDb();

  // Crear el DexieAdapter (nueva arquitectura)
  _dexieAdapter = new DexieAdapter(db);

  // Crear repositorio de perfiles primero (para inyección de dependencias)
  const profileRepo = new ProfileRepository(_dexieAdapter);

  const adapters: DatabaseAdapters = {
    database: db,
    // ⭐ Todos los repositorios ahora usan la arquitectura unificada
    profileRepository: profileRepo,
    // Repositorios principales
    ubicacionRepository: new UbicacionRepository(_dexieAdapter, profileRepo),
    unidadRepository: new UnidadRepository(_dexieAdapter, profileRepo),
    empresaRepository: new EmpresaRepository(_dexieAdapter),
    animalRepository: new AnimalRepository(_dexieAdapter, profileRepo),
    animalHistoricoRepository: new AnimalHistoricoRepository(_dexieAdapter),
    operacionRepository: new OperacionCompraVentaRepository(_dexieAdapter, profileRepo),
    // Repositorios de administración
    gastoRepository: new GastoRepository(_dexieAdapter, profileRepo),
    ingresoRepository: new IngresoRepository(_dexieAdapter, profileRepo),
    stockRepository: new StockRepository(_dexieAdapter, profileRepo),
    stockHistoricoRepository: new StockHistoricoRepository(_dexieAdapter, profileRepo),
    categoriaCacheRepository: new CategoriaCacheRepository(_dexieAdapter),
    // Taxonomía de conceptos (spec 2026-08-07)
    conceptoRepository: new ConceptoRepository(_dexieAdapter, profileRepo),
    categoriaRepository: new CategoriaRepository(_dexieAdapter, profileRepo),
    // Repositorios de notas y herramientas
    notaRepository: new NotaRepository(_dexieAdapter, profileRepo),
    eventoRepository: new EventoRepository(_dexieAdapter, profileRepo),
    recordatorioRepository: new RecordatorioRepository(_dexieAdapter, profileRepo),
    lluviaRepository: new LluviaRepository(_dexieAdapter, profileRepo),
    // Declaraciones SIGSA. Requiere la tabla Dexie v30.
    sigsaDeclaracionRepository: new SigsaDeclaracionRepository(_dexieAdapter, profileRepo),
    // Cola de acciones con ejecución server-side. Requiere la tabla Dexie v33.
    syncAccionesRepository: new SyncAccionesRepository(_dexieAdapter, profileRepo),
    // Elementos de mapa (Mapa del Campo). Requiere la tabla Dexie v29.
    elementoMapaRepository: new ElementoMapaRepository(_dexieAdapter, profileRepo),
    // Puntos de control del mapa (observaciones históricas). Requiere las
    // tablas Dexie v33.
    puntoControlRepository: new PuntoControlRepository(_dexieAdapter, profileRepo),
    puntoControlEventoRepository: new PuntoControlEventoRepository(_dexieAdapter, profileRepo),
    // Siembras de cada lote de siembra. Requiere la tabla Dexie v38.
    siembraLoteRepository: new SiembraLoteRepository(_dexieAdapter, profileRepo),
    notaAnclaRepository: new NotaAnclaRepository(_dexieAdapter, profileRepo),
    personaCampoRepository: new PersonaCampoRepository(_dexieAdapter, profileRepo),
    // Apodos de sólo lectura para el autocompletar de la contraparte. Requiere Dexie v37.
    apodoRepository: new ApodoRepository(_dexieAdapter, profileRepo)
  };

  DatabaseFactory.registerAdapters(adapters, 'web' as Platform);

  _isRegistered = true;

  console.log('[Web] Dexie adapters registered successfully (all repositories now use unified architecture)');
}

/**
 * Verifica si los adapters están registrados
 */
export function isDexieAdaptersRegistered(): boolean {
  return _isRegistered;
}

/**
 * Crea y retorna el adaptador de sincronización para web
 */
export function createSyncAdapter(): DexieSyncAdapter {
  return new DexieSyncAdapter();
}

// Re-exportar todo desde los adapters para conveniencia
export {
  DexieDatabase,
  getDexieDatabase,
  DexieSyncAdapter
};
